import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';

import MenuEditorSidepanel from 'modules/menu/components/MenuEditorSidepanel';
import { useEditOptionsTracking } from 'modules/menu/hooks/telemetryHooks';
import { updateOption } from 'modules/shared-menu/api';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { buildUpdateOptionFormData, formValuesToUpdateOptionPayload, OptionForm, OptionFormData } from './OptionForm';
import { OptionView } from './OptionView';
import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { Option } from '../types';

type Props = {
  activeOption: Option;
  onClose: () => void;
};

const EditOptionSidePanel = ({ activeOption, onClose }: Props) => {
  const { selection, updateSelectionData } = useSelectionData();
  const trackEditOption = useEditOptionsTracking();
  const [modifiedFields, setModifiedFields] = useState<string[]>([]);
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();

  const {
    mutateAsync: handleSubmit,
    error,
    isLoading,
  } = useMutation(
    (formData: OptionFormData) =>
      updateOption(
        selection.id,
        activeOption.id,
        formValuesToUpdateOptionPayload(selection.primary_language, activeOption, formData),
      ),
    {
      onSuccess: updatedOption => {
        updateSelectionData({ actionType: 'updateOption', payload: updatedOption });
        trackEditOption(activeOption.id, modifiedFields);
        onClose();
      },
    },
  );

  if (isSharedVenue || !menuPermissions.contentEdit) {
    return (
      <MenuEditorSidepanel id={activeOption.id} title={activeOption.name[0].value} onClose={onClose}>
        <OptionView language={selection.primary_language} option={activeOption} />
      </MenuEditorSidepanel>
    );
  }

  return (
    <MenuEditorSidepanel id={activeOption.id} title={activeOption.name[0].value} onClose={onClose}>
      {({ editing, setEditing }) =>
        editing ? (
          <OptionForm
            initialValues={buildUpdateOptionFormData(selection.primary_language, activeOption)}
            activeOption={activeOption}
            onCancel={() => {
              setEditing(false);
            }}
            onSubmit={async (formData, modifiedFields) => {
              setModifiedFields(modifiedFields);
              await handleSubmit(formData);
              setEditing(false);
            }}
            error={error}
            submitting={isLoading}
            mode="edit"
          />
        ) : (
          <OptionView language={selection.primary_language} option={activeOption} />
        )
      }
    </MenuEditorSidepanel>
  );
};

export default EditOptionSidePanel;
